/**
 * Parameter Validation Utilities
 * Provides validation and clamping functions for instrument parameters
 */
import type { InstrumentParameters, ADSRSettings, OscillatorSettings, FilterSettings, EffectsSettings, ParameterDefinition, } from '../types/instruments';
/**
 * Clamp a value between min and max
 */
export function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
}
/**
 * Validate and clamp a number value
 * Returns the clamped value and a warning message if clamping occurred
 */
export function validateNumber(value: number, min: number, max: number, paramName: string): {
    value: number;
    warning?: string;
} {
    if (typeof value !== 'number' || isNaN(value)) {
        console.warn(`Invalid ${paramName}: ${value}. Using default: ${min}`);
        return { value: min, warning: `Invalid ${paramName}, using minimum value` };
    }
    const clamped = clamp(value, min, max);
    if (clamped !== value) {
        console.warn(`${paramName} value ${value} out of range [${min}, ${max}]. Clamped to ${clamped}`);
        return { value: clamped, warning: `${paramName} adjusted to valid range` };
    }
    return { value: clamped };
}
/**
 * Validate ADSR settings
 */
export function validateADSR(adsr: Partial<ADSRSettings> | undefined): ADSRSettings {
    const defaults: ADSRSettings = {
        attack: 0.01,
        decay: 0.1,
        sustain: 0.7,
        release: 0.1,
    };
    if (!adsr) {
        return defaults;
    }
    try {
        return {
            attack: validateNumber(adsr.attack ?? defaults.attack, 0.001, 2.0, 'ADSR attack').value,
            decay: validateNumber(adsr.decay ?? defaults.decay, 0.001, 2.0, 'ADSR decay').value,
            sustain: validateNumber(adsr.sustain ?? defaults.sustain, 0.0, 1.0, 'ADSR sustain').value,
            release: validateNumber(adsr.release ?? defaults.release, 0.001, 2.0, 'ADSR release').value,
        };
    }
    catch (error) {
        console.error('Error validating ADSR settings:', error);
        return defaults;
    }
}
/**
 * Validate oscillator settings
 */
export function validateOscillator(oscillator: Partial<OscillatorSettings> | undefined): OscillatorSettings {
    if (!oscillator) {
        return {};
    }
    try {
        const validated: OscillatorSettings = {};
        if (oscillator.type) {
            const validTypes = ['sine', 'square', 'sawtooth', 'triangle', 'pulse'];
            if (validTypes.includes(oscillator.type)) {
                validated.type = oscillator.type;
            }
            else {
            }
        }
        if (oscillator.pulseWidth !== undefined) {
            validated.pulseWidth = validateNumber(oscillator.pulseWidth, 0.0, 1.0, 'Pulse width').value;
        }
        if (oscillator.harmonicity !== undefined) {
            validated.harmonicity = validateNumber(oscillator.harmonicity, 0.5, 8.0, 'Harmonicity').value;
        }
        if (oscillator.modulationIndex !== undefined) {
            validated.modulationIndex = validateNumber(oscillator.modulationIndex, 0, 20, 'Modulation index').value;
        }
        if (oscillator.detune !== undefined) {
            validated.detune = validateNumber(oscillator.detune, -1200, 1200, 'Detune').value;
        }
        return validated;
    }
    catch (error) {
        console.error('Error validating oscillator settings:', error);
        return {};
    }
}
/**
 * Validate filter settings
 */
export function validateFilter(filter: Partial<FilterSettings> | undefined): FilterSettings {
    if (!filter) {
        return {};
    }
    try {
        const validated: FilterSettings = {};
        if (filter.type) {
            const validTypes = ['lowpass', 'highpass', 'bandpass'];
            if (validTypes.includes(filter.type)) {
                validated.type = filter.type;
            }
            else {
            }
        }
        if (filter.frequency !== undefined) {
            validated.frequency = validateNumber(filter.frequency, 20, 20000, 'Filter frequency').value;
        }
        if (filter.resonance !== undefined) {
            validated.resonance = validateNumber(filter.resonance, 0.1, 10, 'Filter resonance').value;
        }
        if (filter.envelopeAmount !== undefined) {
            validated.envelopeAmount = validateNumber(filter.envelopeAmount, 0, 1, 'Filter envelope amount').value;
        }
        return validated;
    }
    catch (error) {
        console.error('Error validating filter settings:', error);
        return {};
    }
}
/**
 * Validate effects settings
 */
export function validateEffects(effects: Partial<EffectsSettings> | undefined): EffectsSettings {
    if (!effects) {
        return {};
    }
    try {
        const validated: EffectsSettings = {};
        if (effects.bitCrush !== undefined) {
            validated.bitCrush = Math.round(validateNumber(effects.bitCrush, 1, 16, 'Bit crush').value);
        }
        if (effects.distortion !== undefined) {
            validated.distortion = validateNumber(effects.distortion, 0, 1, 'Distortion').value;
        }
        if (effects.chorus !== undefined) {
            validated.chorus = Boolean(effects.chorus);
        }
        if (effects.delay) {
            validated.delay = effects.delay; // Delay settings are complex, validate separately if needed
        }
        return validated;
    }
    catch (error) {
        console.error('Error validating effects settings:', error);
        return {};
    }
}
/**
 * Validate complete instrument parameters
 */
export function validateInstrumentParameters(parameters: Partial<InstrumentParameters> | undefined): InstrumentParameters {
    try {
        return {
            adsr: validateADSR(parameters?.adsr),
            oscillator: validateOscillator(parameters?.oscillator),
            filter: validateFilter(parameters?.filter),
            effects: validateEffects(parameters?.effects),
        };
    }
    catch (error) {
        console.error('Error validating instrument parameters:', error);
        // Return safe defaults
        return {
            adsr: { attack: 0.01, decay: 0.1, sustain: 0.7, release: 0.1 },
            oscillator: {},
            filter: {},
            effects: {},
        };
    }
}
/**
 * Validate parameter against its definition
 */
export function validateParameterAgainstDefinition(value: number, definition: ParameterDefinition): {
    value: number;
    warning?: string;
} {
    return validateNumber(value, definition.min, definition.max, definition.name);
}
/**
 * Check if parameters are valid (basic structure check)
 */
export function areParametersValid(parameters: any): parameters is InstrumentParameters {
    if (!parameters || typeof parameters !== 'object') {
        return false;
    }
    // Check ADSR if present
    if (parameters.adsr) {
        const adsr = parameters.adsr;
        if (typeof adsr.attack !== 'number' ||
            typeof adsr.decay !== 'number' ||
            typeof adsr.sustain !== 'number' ||
            typeof adsr.release !== 'number') {
            return false;
        }
    }
    return true;
}
